import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { endOfToday, format } from "date-fns";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { useState, useEffect, useCallback } from "react";
import { useCreate, useNotify, Confirm } from "react-admin";
import { styled } from "@mui/material/styles";
import mbaas from "../../../../provider/Mbaas";

type userData = {
  id: string;
};

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CustomDialogTitle = ({ children, onClose, ...other }: any) => {
  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export const UploadDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [userData, setUserData] = useState<userData>({
    id: "",
  });
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openUploadConfirm, setOpenUploadConfirm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const notify = useNotify();

  const [uploadData, { data, error, isSuccess }] = useCreate();
  if (error) {
    throw new Error("error request");
  }

  const getUserData = useCallback(async () => {
    const res = await mbaas.client.user.get();
    if (res.data) {
      setUserData({ id: res?.data?.id });
    }
    if (res?.error) {
      notify(`${res?.error?.title}`, { type: "error" });
    }
  }, [notify]);

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  useEffect(() => {
    if (isSuccess === true) setOpenConfirm(true);
  }, [isSuccess]);

  const handleClose = () => {
    setFileUpload(null);
    onClose();
  };

  const handleUploadClick = () => {
    setOpenUploadConfirm(true);
  };

  const handleConfirmUpload = () => {
    setOpenUploadConfirm(false);
    handleUpload();
  };

  const handleUpload = async () => {
    setIsLoading(true);
    await mbaas.client.storage.upload({
      file: fileUpload,
      params: {
        filename: `upload-reward-code-${format(
          new Date(endOfToday()),
          "yyyy-MM-dd"
        )}`,
        bucket: "documents",
        signed: false,
        expire: "60",
      },
    });

    uploadData("documentsBatch", {
      data: {
        type: "REWARD_CODE_BATCH",
        author_id: userData.id,
        filename: `upload-reward-code-${format(
          new Date(endOfToday()),
          "yyyy-MM-dd"
        )}`,
        status: "QUEUED",
      },
    });
    setIsLoading(false);
  };

  const downloadTemplate = () => {
    const csvContent = "reward_catalogue_id,reward_code";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "reward_codes_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ConfirmDialogContent = () => {
    return (
      <Paper elevation={0}>
        <Typography variant="body1" gutterBottom>
          Your file is being processed with name "{data.filename}", you can see
          the progress by visiting batch page.
        </Typography>
      </Paper>
    );
  };

  const navigateToBatchPage = () => {
    navigate(encodeURI(`/documents`));
  };

  return (
    <>
      <CustomDialog onClose={handleClose} open={open} fullWidth maxWidth="sm">
        <CustomDialogTitle onClose={handleClose}>
          Upload Reward Codes
        </CustomDialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Upload a CSV file containing reward codes. Each line should
              contain one code.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={downloadTemplate}
                size="small"
                disabled={isLoading}
              >
                Download Template
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 1,
                  p: 2,
                  textAlign: "center",
                  opacity: isLoading ? 0.5 : 1,
                }}
              >
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => setFileUpload(e.target.files?.[0] || null)}
                  style={{ display: "none" }}
                  id="file-upload"
                  disabled={isLoading}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={isLoading}
                  >
                    Choose File
                  </Button>
                </label>
                {fileUpload && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {fileUpload.name}
                  </Typography>
                )}
                {isLoading && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="primary">
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Processing upload...
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadClick}
            startIcon={
              isLoading ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
            variant="contained"
            disabled={!fileUpload || isLoading}
          >
            {isLoading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </CustomDialog>
      <Confirm
        isOpen={openUploadConfirm}
        title="Confirm Upload"
        content={
          <Paper elevation={0}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to upload this file? This process cannot be
              reverted once started.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              File: {fileUpload?.name}
            </Typography>
          </Paper>
        }
        onClose={() => setOpenUploadConfirm(false)}
        confirm="Yes, Upload"
        onConfirm={handleConfirmUpload}
        confirmColor="primary"
        cancel="Cancel"
      />
      <Confirm
        isOpen={openConfirm}
        title="Dear User,"
        content={<ConfirmDialogContent />}
        onClose={() => setOpenConfirm(false)}
        confirm="Go To Batch"
        onConfirm={navigateToBatchPage}
      />
    </>
  );
};
